import { useEffect, useRef, useState, useCallback } from "react";
import { useExam } from "../context/ExamContext";
import { useUser } from "../context/userContext";
import { KEYS } from "../constants/storageKeys";

/**
 * Hook for managing exam ProductSession lifecycle
 *
 * NOTE: Periodic heartbeat has been removed and consolidated into the WebSocket
 * ExamTabLock system (useExamTabLock.js). The WebSocket heartbeat now updates
 * both ExamTabLock.lastHeartbeat AND ProductSession.lastHeartbeat.
 *
 * This hook now only handles:
 * - Starting the ProductSession when exam begins
 * - Finishing the ProductSession when exam ends
 */
export function useExamHeartbeat({
  enabled = true,
  apiBase = process.env.REACT_APP_API_URL,
  sessionsPath = "/sessions",
  isInstantExam = false,
} = {}) {
  const { user } = useUser();
  const { uprId, isDemoExam } = useExam();
  const [session, setSession] = useState(null);
  const startedRef = useRef(false);
  const sessionsBaseUrl = `${apiBase}${sessionsPath}`;

  async function postKeepAlive(url, body) {
    try {
      const payload = JSON.stringify(body);
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        const ok = navigator.sendBeacon(url, blob);
        if (ok) return true;
      }
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
        credentials: "include",
      });
      return true;
    } catch {
      return false;
    }
  }

  const startSession = useCallback(async () => {
    if (!enabled || !uprId || !user?._id || startedRef.current || isDemoExam)
      return;

    // For instant exams, check if there's already an existing session
    if (isInstantExam) {
      const existingSessionId = sessionStorage.getItem(KEYS.PS_SESSION_ID);
      const existingSessionToken = sessionStorage.getItem(KEYS.PS_SESSION_TOKEN);

      if (existingSessionId && existingSessionToken) {
        // Use existing session for instant exams
        setSession({
          sessionId: existingSessionId,
          sessionToken: existingSessionToken,
        });
        startedRef.current = true;
        return;
      }
    }

    startedRef.current = true;
    try {
      const res = await fetch(`${sessionsBaseUrl}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userProductRegistrationId: uprId,
          userId: user._id,
        }),
      });
      if (!res.ok) throw new Error(`start failed ${res.status}`);
      const data = await res.json();
      setSession({
        sessionId: data.sessionId,
        sessionToken: data.sessionToken,
      });
      sessionStorage.setItem(KEYS.PS_SESSION_ID, data.sessionId);
      sessionStorage.setItem(KEYS.PS_SESSION_TOKEN, data.sessionToken);
    } catch (e) {
      console.error("Session startSession error:", e?.message || e);
      startedRef.current = false;
    }
  }, [enabled, uprId, user?._id, isDemoExam, isInstantExam, sessionsBaseUrl]);

  const finishSession = useCallback(async () => {
    const sid = session?.sessionId || sessionStorage.getItem(KEYS.PS_SESSION_ID);
    const tok =
      session?.sessionToken || sessionStorage.getItem(KEYS.PS_SESSION_TOKEN);
    if (!sid || !tok) return;
    await postKeepAlive(
      `${sessionsBaseUrl}/${encodeURIComponent(sid)}/finish`,
      { sessionToken: tok }
    );
    sessionStorage.removeItem(KEYS.PS_SESSION_ID);
    sessionStorage.removeItem(KEYS.PS_SESSION_TOKEN);
  }, [session?.sessionId, session?.sessionToken, sessionsBaseUrl]);

  // Start session when enabled
  useEffect(() => {
    if (!enabled || isDemoExam) return;
    startSession();
  }, [enabled, isDemoExam, startSession]);

  // NOTE: Periodic REST heartbeat removed - now handled by WebSocket in useExamTabLock
  // The ExamTabLockService.heartbeat() method updates both ExamTabLock and ProductSession

  return {
    isSessionStarted: !!session?.sessionId,
    session,
    finishSession,
  };
}
